import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
} from 'react-bootstrap';

import { NavLink } from 'react-router-dom';
import { FaEdit, FaEye } from 'react-icons/fa';

import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';

import API from '../../api/axios';
import useCompanyContext from '../../hooks/useCompanyContext';

const INITIAL_META = {
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 1,
};

const INITIAL_PAGE = {
  page_number: 1,
  total_pages: 1,
};

const INITIAL_EXACT_SEARCH = {
  brandName: '',
  modelName: '',
  location: '',
};

const EXACT_SEARCH_FIELDS = [
  {
    key: 'brandName',
    label: 'Brand name',
    placeholder: 'Exact brand name',
  },
  {
    key: 'modelName',
    label: 'Model name',
    placeholder: 'Exact model name',
  },
  {
    key: 'location',
    label: 'Shop location',
    placeholder: 'Exact location or city',
  },
];

const normalizeShop = (
  shopMotorcycle,
  model,
) => {
  const shop =
    shopMotorcycle?.Shop || {};

  const location =
    shop?.Location || {};

  return {
    id: shopMotorcycle.id,

    model_id:
      model.id,

    model_brand:
      model?.Brand?.name ||
      '',

    model_name:
      model.name ||
      '',

    shop_id:
      shop.id,

    shop_name:
      shop.name ||
      '',

    shop_location:
      location.name ||
      location.slug ||
      '',

    city_name:
      location?.City?.name ||
      '',

    raw:
      shopMotorcycle,
  };
};

const MotorcyclesPage = () => {
  const {
    currentCompanyID,
    hasSpecificCompany,
    buildCompanyUrl,
  } = useCompanyContext();

  const [rows, setRows] =
    useState([]);

  const [meta, setMeta] =
    useState(INITIAL_META);

  const [page, setPage] =
    useState(INITIAL_PAGE);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [
    globalSearch,
    setGlobalSearch,
  ] = useState('');

  const [
    exactSearch,
    setExactSearch,
  ] = useState(
    INITIAL_EXACT_SEARCH,
  );

  const [
    expandedModelIds,
    setExpandedModelIds,
  ] = useState(
    () => new Set(),
  );

  const requestRef =
    useRef(0);

  const previousCompanyIdRef =
    useRef(
      currentCompanyID,
    );

  const skipNextFetchRef =
    useRef(false);

  const resetTableState =
    useCallback(
      (
        message =
          'Please select a supplier first',
      ) => {
        /*
         * 让之前仍在进行的请求失效。
         */
        requestRef.current += 1;

        setRows([]);

        setMeta(
          INITIAL_META,
        );

        setPage((prev) => {
          if (
            prev.page_number === 1 &&
            prev.total_pages === 1
          ) {
            return prev;
          }

          return {
            ...INITIAL_PAGE,
          };
        });

        setExpandedModelIds(
          new Set(),
        );

        setError(
          message,
        );

        setLoading(
          false,
        );
      },
      [],
    );

  const fetchMotorcycles =
    useCallback(
      async () => {
        if (
          !hasSpecificCompany ||
          Number(
            currentCompanyID,
          ) === 0
        ) {
          resetTableState(
            'Please select a supplier first',
          );

          return;
        }

        const requestId =
          ++requestRef.current;

        try {
          setLoading(
            true,
          );

          setError('');

          const params =
            new URLSearchParams();

          params.set(
            'page',
            String(
              page.page_number ||
              1,
            ),
          );

          /*
           * 此接口原本就支持 pageSize，
           * 保留现有 API contract。
           */
          params.set(
            'pageSize',
            String(
              meta.pageSize ||
              10,
            ),
          );

          params.set(
            'supplierId',
            String(
              currentCompanyID,
            ),
          );

          const normalizedGlobalSearch =
            String(
              globalSearch ||
              '',
            ).trim();

          if (
            normalizedGlobalSearch
          ) {
            params.set(
              'q',
              normalizedGlobalSearch,
            );
          }

          Object.entries(
            exactSearch,
          ).forEach(
            ([
              key,
              value,
            ]) => {
              const normalized =
                String(
                  value ||
                  '',
                ).trim();

              if (
                normalized
              ) {
                params.set(
                  key,
                  normalized,
                );
              }
            },
          );

          const {
            data,
          } =
            await API.get(
              `/shop-motorcycles?${params.toString()}`,
            );

          /*
           * 如果期间已经发送了更新请求，
           * 忽略旧结果。
           */
          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          const normalizedRows =
            (
              data?.data ||
              []
            ).map(
              (group) => {
                const model =
                  group?.motorcycleModel ||
                  {};

                const shops =
                  (
                    group?.shops ||
                    []
                  ).map(
                    (
                      shopMotorcycle,
                    ) =>
                      normalizeShop(
                        shopMotorcycle,
                        model,
                      ),
                  );

                return {
                  id:
                    model.id,

                  model_id:
                    model.id,

                  model_brand:
                    model
                      ?.Brand
                      ?.name ||
                    '',

                  model_name:
                    model.name ||
                    '',

                  model_type:
                    model.type ||
                    '',

                  media_url:
                    model
                      ?.Media
                      ?.url ||
                    '',

                  shops,
                };
              },
            );

          setRows(
            normalizedRows,
          );

          const pagination = {
            total:
              data?.total ??
              0,

            page:
              data?.page ??
              1,

            pageSize:
              data?.pageSize ??
              10,

            totalPages:
              data?.totalPages ??
              1,
          };

          setMeta(
            pagination,
          );

          setPage(
            (prev) => {
              const nextPage =
                pagination.page ||
                1;

              const nextTotalPages =
                pagination.totalPages ||
                1;

              if (
                prev.page_number ===
                nextPage &&
                prev.total_pages ===
                nextTotalPages
              ) {
                return prev;
              }

              return {
                ...prev,

                page_number:
                  nextPage,

                total_pages:
                  nextTotalPages,
              };
            },
          );
        } catch (e) {
          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          setError(
            e?.response?.data
              ?.message ||
            e?.message ||
            'Failed to load motorcycles',
          );

          setRows([]);
        } finally {
          if (
            requestId ===
            requestRef.current
          ) {
            setLoading(
              false,
            );
          }
        }
      },
      [
        currentCompanyID,
        hasSpecificCompany,
        page.page_number,
        meta.pageSize,
        globalSearch,
        exactSearch,
        resetTableState,
      ],
    );

  const handleSetGlobalSearch =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setGlobalSearch(
          (prev) =>
            typeof valueOrUpdater ===
              'function'
              ? valueOrUpdater(
                prev,
              )
              : valueOrUpdater,
        );

        setExpandedModelIds(
          new Set(),
        );

        setPage(
          (prev) =>
            prev.page_number ===
              1
              ? prev
              : {
                ...prev,
                page_number:
                  1,
              },
        );
      },
      [],
    );

  const handleSetExactSearch =
    useCallback(
      (
        valueOrUpdater,
      ) => {
        setExactSearch(
          (prev) =>
            typeof valueOrUpdater ===
              'function'
              ? valueOrUpdater(
                prev,
              )
              : valueOrUpdater,
        );

        setExpandedModelIds(
          new Set(),
        );

        setPage(
          (prev) =>
            prev.page_number ===
              1
              ? prev
              : {
                ...prev,
                page_number:
                  1,
              },
        );
      },
      [],
    );

  const toggleExpanded =
    useCallback(
      (modelId) => {
        setExpandedModelIds(
          (previous) => {
            const next =
              new Set(
                previous,
              );

            if (
              next.has(
                modelId,
              )
            ) {
              next.delete(
                modelId,
              );
            } else {
              next.add(
                modelId,
              );
            }

            return next;
          },
        );
      },
      [],
    );

  /*
   * Company 切换：
   *
   * - 清除展开状态
   * - 如果切到 All Companies，则清空列表
   * - 如果当前不在第一页，先回第一页
   */
  useEffect(() => {
    if (
      previousCompanyIdRef
        .current ===
      currentCompanyID
    ) {
      return;
    }

    previousCompanyIdRef.current =
      currentCompanyID;

    setExpandedModelIds(
      new Set(),
    );

    if (
      !hasSpecificCompany ||
      Number(
        currentCompanyID,
      ) === 0
    ) {
      resetTableState(
        'Please select a supplier first',
      );

      return;
    }

    if (
      page.page_number !==
      1
    ) {
      skipNextFetchRef.current =
        true;

      setPage(
        (prev) => ({
          ...prev,
          page_number:
            1,
        }),
      );
    }
  }, [
    currentCompanyID,
    hasSpecificCompany,
    page.page_number,
    resetTableState,
  ]);

  /*
   * 唯一的数据请求 effect。
   */
  useEffect(() => {
    if (
      skipNextFetchRef.current
    ) {
      skipNextFetchRef.current =
        false;

      return;
    }

    fetchMotorcycles();
  }, [fetchMotorcycles]);

  const columns =
    useMemo(
      () => [
        {
          keys: [
            'model_id',
            'model_brand',
            'model_name',
          ],

          label:
            'Model Info',

          render: (
            item,
          ) => (
            <div className="d-flex align-items-center">
              <Badge
                bg="secondary"
                style={{
                  minWidth:
                    '100px',
                  fontSize:
                    14,
                }}
              >
                {item.model_brand ||
                  '—'}
              </Badge>

              <span className="ms-2">
                {item.model_name ||
                  '—'}
              </span>
            </div>
          ),
        },

        {
          keys: [
            'shops',
          ],

          label:
            'Shop Info',

          render: (
            item,
          ) => {
            const expanded =
              expandedModelIds.has(
                item.model_id,
              );

            const shops =
              Array.isArray(
                item.shops,
              )
                ? item.shops
                : [];

            const visibleShops =
              expanded
                ? shops
                : shops.slice(
                  0,
                  3,
                );

            const hiddenCount =
              Math.max(
                0,
                shops.length -
                3,
              );

            return (
              <div>
                {visibleShops.map(
                  (
                    shop,
                  ) => (
                    <div
                      key={
                        shop.id
                      }
                      className="d-flex align-items-center gap-2 py-1"
                    >
                      <Badge
                        bg="primary"
                        className="text-truncate"
                        style={{
                          maxWidth:
                            150,
                        }}
                      >
                        {shop.shop_location ||
                          'No location'}
                      </Badge>

                      <span
                        className="text-truncate"
                        title={
                          shop.shop_name
                        }
                      >
                        {shop.shop_name ||
                          '—'}
                      </span>
                    </div>
                  ),
                )}

                {hiddenCount >
                  0 && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="px-0 mt-1 text-decoration-none"
                      onClick={(
                        event,
                      ) => {
                        event.stopPropagation();

                        toggleExpanded(
                          item.model_id,
                        );
                      }}
                    >
                      {expanded
                        ? 'Show less'
                        : `Show all ${shops.length} shops`}
                    </Button>
                  )}
              </div>
            );
          },
        },

        {
          key:
            'actions',

          label:
            'Actions',

          render: (
            item,
          ) => (
            <div
              className="d-flex gap-2 align-items-center"
              onClick={(
                event,
              ) =>
                event.stopPropagation()
              }
            >
              <NavLink
                to={buildCompanyUrl(
                  `/motorcycles/${item.model_id}`
                )}
                className="btn btn-view p-0"
                title="View"
              >
                <FaEye
                  size={20}
                />
              </NavLink>

              <NavLink
                to={buildCompanyUrl(
                  `/motorcycles/${item.model_id}`
                )}
                className="btn btn-edit p-0"
                title="Edit"
              >
                <FaEdit
                  size={20}
                  style={{
                    marginLeft: 4,
                  }}
                />
              </NavLink>
            </div>
          ),
        },
      ],
      [
        expandedModelIds,
        toggleExpanded,
        buildCompanyUrl,
      ],
    );

  return (
    <Container
      fluid
      className="d-flex flex-column"
    >
      <Row className="mb-4">
        <Col sm={8}>
          <span className="fw-semibold">
            Motorcycles List
          </span>
        </Col>

        <Col
          sm={4}
          className="text-end"
        >
          <span className="fw-semibold">
            <NavLink
              to={buildCompanyUrl(
                "/dashboard"
              )}
              className="block-link"
            >
              Dashboard
            </NavLink>

            {' - Motorcycles List'}
          </span>
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <Card className="h-100 p-2">
            <Card.Body className="px-2 pt-2 pb-5">
              <Row>
                <Col md={9}>
                  <SmartTopTool
                    advancedSearch
                    globalSearch={
                      globalSearch
                    }
                    setGlobalSearch={
                      handleSetGlobalSearch
                    }
                    globalSearchPlaceholder="Search model, brand or shop location..."
                    exactSearch={
                      exactSearch
                    }
                    setExactSearch={
                      handleSetExactSearch
                    }
                    exactSearchFields={
                      EXACT_SEARCH_FIELDS
                    }
                    debounceMs={
                      400
                    }
                  />
                </Col>

                {hasSpecificCompany &&
                  Number(
                    currentCompanyID,
                  ) !== 0 && (
                    <Col
                      md={3}
                      className="d-flex justify-content-end gap-2 align-items-start"
                    >
                      <NavLink
                        to={buildCompanyUrl("/motorcycles/new")}
                        className="btn btn-success d-inline-flex align-items-center justify-content-center"
                        style={{
                          height:
                            40,
                        }}
                      >
                        + New
                      </NavLink>
                    </Col>
                  )}

                <Col md={12}>
                  <SmartTable
                    data={rows}
                    columns={
                      columns
                    }
                    loading={
                      loading
                    }
                    emptyState={
                      <div className="text-center py-5 text-muted">
                        <p>
                          {error ||
                            'No matching motorcycles found'}
                        </p>
                      </div>
                    }
                  />
                </Col>

                <Col
                  md={12}
                  className="d-flex justify-content-end"
                >
                  <SmartPagination
                    page={page}
                    setPage={
                      setPage
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MotorcyclesPage;